/**
 * PeerJS star: one host, everyone else connects to them.
 * Free cloud broker (0.peerjs.com). Fine for ~10–20 people.
 *
 * Default public lobby: ONLYBUILDS. Room → Peer id `ob5-{room}`.
 * Modes: ctx.net.send(...) / ctx.net.on(...)
 */

export type NetMsg = {
  t: string;
  from?: string;
  [k: string]: unknown;
};

export type NetPlayer = { id: string; name: string };

type Handler = (msg: NetMsg) => void;

const PREFIX = "ob5-";
const MAX = 20;

/** Shared demo lobby — first tab hosts, everyone else piles in. */
export const PUBLIC_ROOM = "ONLYBUILDS";

export function normalizeRoom(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

export function roomFromUrl(): string | null {
  try {
    const r = new URLSearchParams(window.location.search).get("room");
    return r ? normalizeRoom(r) : null;
  } catch {
    return null;
  }
}

export class Net {
  host = false;
  room = "";
  selfId = "";
  status = "offline";
  players: NetPlayer[] = [];

  private peer: Peer | null = null;
  private hostConn: DataConnection | null = null;
  private guests = new Map<string, DataConnection>();
  private handlers = new Set<Handler>();
  private statusCbs = new Set<() => void>();
  private chain: Promise<unknown> = Promise.resolve();

  get connected(): boolean {
    return this.status === "host" || this.status === "joined";
  }

  get count(): number {
    return 1 + (this.host ? this.guests.size : Math.max(0, this.players.length - 1));
  }

  on(fn: Handler): () => void {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  onStatus(fn: () => void): () => void {
    this.statusCbs.add(fn);
    return () => this.statusCbs.delete(fn);
  }

  send(msg: NetMsg) {
    const packed: NetMsg = { ...msg, from: this.selfId };
    if (this.host) {
      this.relay(packed);
    } else if (this.hostConn?.open) {
      this.hostConn.send(packed);
    }
  }

  /** Host a new (or given) room. */
  async hostRoom(code?: string): Promise<string> {
    return this.serial(() => this.hostRoomNow(code));
  }

  /** Join an existing host room. */
  async joinRoom(code: string): Promise<void> {
    return this.serial(() => this.joinRoomNow(code));
  }

  /** Claim the public room as host; if someone already has it, join them. */
  async ensurePublic(): Promise<void> {
    return this.serial(async () => {
      if (this.connected && this.room === PUBLIC_ROOM) return;
      try {
        await this.hostRoomNow(PUBLIC_ROOM);
      } catch {
        await this.joinRoomNow(PUBLIC_ROOM);
      }
    });
  }

  private async hostRoomNow(code?: string): Promise<string> {
    const room = normalizeRoom(code || PUBLIC_ROOM) || PUBLIC_ROOM;
    if (this.host && this.room === room && this.peer) return room;
    this.teardown();
    await wait(180);
    await this.openPeer(PREFIX + room);
    this.host = true;
    this.room = room;
    this.selfId = this.peer!.id;
    this.status = "host";
    this.players = [{ id: this.selfId, name: "host" }];
    this.peer!.on("connection", (conn) => this.acceptGuest(conn));
    this.pushUrl();
    this.bump();
    return room;
  }

  private async joinRoomNow(code: string): Promise<void> {
    const room = normalizeRoom(code);
    if (this.connected && this.room === room && !this.host) return;
    this.teardown();
    await wait(120);
    if (!room) throw new Error("need a room code");
    await this.openPeer();
    this.host = false;
    this.room = room;
    this.selfId = this.peer!.id;
    this.status = "connecting";
    this.bump();

    const conn = this.peer!.connect(PREFIX + room, { reliable: true });
    this.hostConn = conn;
    await new Promise<void>((resolve, reject) => {
      const t = window.setTimeout(() => reject(new Error("host not found / timeout")), 3500);
      conn.on("open", () => {
        window.clearTimeout(t);
        resolve();
      });
      conn.on("error", (err) => {
        window.clearTimeout(t);
        reject(err);
      });
    });
    this.wire(conn);
    conn.send({ t: "hello", from: this.selfId, name: "guest" } satisfies NetMsg);
    this.status = "joined";
    this.pushUrl();
    this.bump();
  }

  destroy() {
    this.teardown();
    this.bump();
  }

  private teardown() {
    for (const c of this.guests.values()) {
      try {
        c.close();
      } catch {
        /* ignore */
      }
    }
    this.guests.clear();
    try {
      this.hostConn?.close();
    } catch {
      /* ignore */
    }
    this.hostConn = null;
    const old = this.peer;
    this.peer = null;
    try {
      old?.destroy();
    } catch {
      /* ignore */
    }
    this.host = false;
    this.room = "";
    this.selfId = "";
    this.status = "offline";
    this.players = [];
  }

  shareUrl(): string {
    const u = new URL(window.location.href);
    u.searchParams.set("room", this.room || PUBLIC_ROOM);
    return u.toString();
  }

  async shareInvite(): Promise<"shared" | "copied" | "shown"> {
    const url = this.shareUrl();
    const room = this.room || PUBLIC_ROOM;
    const data = {
      title: "OnlyBuilds · Plush Arena",
      text: `Jump in the public pile — room ${room}`,
      url,
    };
    const nav = navigator as Navigator & {
      share?: (d: ShareData) => Promise<void>;
      canShare?: (d: ShareData) => boolean;
    };
    if (typeof nav.share === "function") {
      try {
        if (!nav.canShare || nav.canShare(data)) {
          await nav.share(data);
          return "shared";
        }
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return "shown";
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      return "copied";
    } catch {
      return "shown";
    }
  }

  private acceptGuest(conn: DataConnection) {
    if (this.guests.size >= MAX - 1) {
      conn.close();
      return;
    }
    this.wire(conn);
    if (conn.open) this.guests.set(conn.peer, conn);
    else conn.on("open", () => this.guests.set(conn.peer, conn));
  }

  private wire(conn: DataConnection) {
    conn.on("data", (raw) => {
      const msg = coerce(raw);
      if (!msg) return;
      if (this.host) {
        if (msg.t === "hello") {
          this.players = [
            { id: this.selfId, name: "host" },
            ...[...this.guests.keys(), conn.peer]
              .filter((id, i, a) => a.indexOf(id) === i)
              .map((id) => ({ id, name: "guest" })),
          ];
          this.relay({ t: "roster", players: this.players });
          this.emit({ t: "roster", players: this.players });
          this.bump();
        }
        this.relay(msg, conn.peer);
      } else if (msg.t === "roster" && Array.isArray(msg.players)) {
        this.players = msg.players as NetPlayer[];
        this.bump();
      }
      this.emit(msg);
    });
    conn.on("close", () => {
      this.guests.delete(conn.peer);
      if (this.host) {
        this.players = this.players.filter((p) => p.id !== conn.peer);
        this.relay({ t: "roster", players: this.players });
        this.emit({ t: "leave", from: conn.peer });
        this.bump();
      } else if (conn === this.hostConn) {
        this.status = "offline";
        this.bump();
      }
    });
  }

  private relay(msg: NetMsg, except?: string) {
    for (const [id, c] of this.guests) {
      if (id === except) continue;
      if (c.open) c.send(msg);
    }
  }

  private emit(msg: NetMsg) {
    for (const fn of this.handlers) fn(msg);
  }

  private bump() {
    for (const fn of this.statusCbs) fn();
  }

  private pushUrl() {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("room", this.room);
      history.replaceState(null, "", u.toString());
    } catch {
      /* ignore */
    }
  }

  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.chain.then(fn, fn);
    this.chain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private openPeer(id?: string): Promise<string> {
    const PeerCtor = window.Peer;
    if (!PeerCtor) return Promise.reject(new Error("PeerJS not loaded"));
    const opts = {
      host: "0.peerjs.com",
      port: 443,
      path: "/",
      secure: true,
      debug: 0,
    };
    return new Promise((resolve, reject) => {
      const peer = id ? new PeerCtor(id, opts) : new PeerCtor(undefined, opts);
      this.peer = peer;
      let settled = false;
      const t = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("PeerJS broker timeout"));
      }, 8000);
      peer.on("open", (openId) => {
        if (this.peer !== peer || settled) return;
        settled = true;
        window.clearTimeout(t);
        resolve(openId);
      });
      peer.on("error", (err) => {
        if (this.peer !== peer || settled) return;
        settled = true;
        window.clearTimeout(t);
        reject(err);
      });
    });
  }
}

function wait(ms: number) {
  return new Promise<void>((r) => window.setTimeout(r, ms));
}

function coerce(raw: unknown): NetMsg | null {
  if (!raw || typeof raw !== "object") return null;
  const t = (raw as NetMsg).t;
  if (typeof t !== "string") return null;
  return raw as NetMsg;
}

export function waitForPeerJs(ms = 5000): Promise<boolean> {
  if (window.Peer) return Promise.resolve(true);
  return new Promise((resolve) => {
    const t0 = performance.now();
    const id = window.setInterval(() => {
      if (window.Peer) {
        window.clearInterval(id);
        resolve(true);
      } else if (performance.now() - t0 > ms) {
        window.clearInterval(id);
        resolve(false);
      }
    }, 40);
  });
}
